import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount } from '@/components/amount';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCan } from '@/lib/permissions';

/**
 * WAVE 4D — THE SALES COMMISSION %, beside the rep fields of the quotation,
 * order and invoice headers.
 *
 * The rule is the server's (apps/api/src/platform/commission.ts): an empty box
 * on a NEW document takes the rep's default (the quotation's sales-rep
 * employee, else the salesperson's linked employee); the rate carries down
 * quotation → order → invoice and onto a credit note; the amount is net ×
 * pct ÷ 100, computed by the server and shown here read-only.
 *
 * Who may type it: `sales.approve` — the permission that already decides the
 * money-shaping acts on a sales document (discount caps, posting). Without it
 * the rate is shown, read-only.
 */
export interface CommissionDoc {
  commissionPct: string | null;
  commissionAmount: string | null;
  currency: string;
}

export interface CommissionInput {
  text: string;
  setText: (value: string) => void;
  /** CREATE: a typed rate, or nothing (= the rep's default). */
  createBody: () => { commissionPct?: string };
  /** PATCH: only when the box moved — a rate, or null to clear. */
  patchBody: () => { commissionPct?: string | null };
}

const PCT_SHAPE = /^\d{1,3}(\.\d{1,2})?$/;

export function useCommissionInput(doc: CommissionDoc | undefined): CommissionInput {
  const saved = doc?.commissionPct ?? '';
  const [text, setText] = useState(saved);
  useEffect(() => {
    setText(saved);
  }, [saved]);
  return {
    text,
    setText,
    createBody: () => (text.trim() === '' ? {} : { commissionPct: text.trim() }),
    patchBody: () => {
      const typed = text.trim();
      if (typed === saved) return {};
      return { commissionPct: typed === '' ? null : typed };
    },
  };
}

export function CommissionField({
  idPrefix,
  doc,
  input,
  disabled,
}: {
  idPrefix: string;
  doc: CommissionDoc | undefined;
  input: CommissionInput;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const canSet = useCan('sales.approve');
  const id = `${idPrefix}-commission`;
  const typed = input.text.trim();
  const invalid = typed !== '' && (!PCT_SHAPE.test(typed) || isOverHundred(typed));
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{t('sales.fCommissionPct')}</Label>
      <Input
        id={id}
        dir="ltr"
        inputMode="decimal"
        className="font-mono"
        value={input.text}
        placeholder={t('sales.commissionDefaultHint')}
        onChange={(event) => input.setText(event.target.value)}
        disabled={disabled || !canSet}
        aria-invalid={invalid || undefined}
        aria-describedby={`${id}-amount`}
      />
      <div id={`${id}-amount`} className="flex items-center gap-2 text-xs text-muted-foreground">
        {invalid ? (
          <span className="text-destructive">{t('sales.errCommissionPct')}</span>
        ) : doc?.commissionAmount ? (
          <>
            <span>{t('sales.commissionAmount')}</span>
            <Amount value={doc.commissionAmount} currency={doc.currency} />
          </>
        ) : null}
      </div>
    </div>
  );
}

/** 0..100 on the STRING (the contract's PctStringSchema rule), never a JS number. */
function isOverHundred(value: string): boolean {
  const [integer = '', fraction = ''] = value.split('.');
  const whole = integer.replace(/^0+(?=\d)/, '');
  if (whole.length < 3) return false;
  return whole === '100' ? !/^0*$/.test(fraction) : true;
}
